import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Activity, BarChart3, Download, Loader2, Mail, RefreshCw, TriangleAlert,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { blobErrorDetail } from '../../lib/blobError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/reports/operational-analysis`;

const STATUS_STYLE = {
  vert: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  orange: 'bg-amber-100 text-amber-700 border-amber-200',
  rouge: 'bg-red-100 text-red-700 border-red-200',
};
const LEVEL_STYLE = {
  critique: 'text-red-700', eleve: 'text-orange-700', moyen: 'text-amber-700', faible: 'text-emerald-700',
};

const download = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  a.remove();
};

/** Analyse opérationnelle IA (volumes, ponctualité, transporteurs, incidents) — staff interne. */
export const OperationalAiAnalysisCard = ({ periodParams, periodLabel, canConfigure }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [recipientsText, setRecipientsText] = useState('');

  const periodReady = Boolean(
    (periodParams?.year && periodParams?.month) || (periodParams?.start_date && periodParams?.end_date)
  );
  const paramsKey = JSON.stringify(periodParams || {});

  const fetchLatest = useCallback(async () => {
    if (!periodReady) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/latest`, { params: periodParams });
      setReport(data?.report ? data : null);
    } catch (err) {
      setReport(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, periodReady]);

  useEffect(() => { fetchLatest(); }, [fetchLatest]);

  useEffect(() => {
    if (!canConfigure) return;
    axios.get(`${API}/schedule`)
      .then(({ data }) => {
        setSchedule(data);
        setRecipientsText((data?.recipients || []).join(', '));
      })
      .catch(() => setSchedule(null));
  }, [canConfigure]);

  const handleGenerate = async () => {
    if (!periodReady) return toast.error('Sélectionnez une période pour lancer l\'analyse');
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/generate`, null, { params: periodParams });
      setReport(data);
      toast.success('Analyse opérationnelle générée');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Analyse IA indisponible');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!periodReady) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/download/pdf`, { params: periodParams, responseType: 'blob' });
      download(res.data, `Analyse_Operationnelle_${(periodLabel || '').replace(/\s+/g, '_')}.pdf`);
      toast.success('Rapport PDF téléchargé');
    } catch (err) {
      toast.error(await blobErrorDetail(err, 'Téléchargement impossible'));
    } finally {
      setDownloading(false);
    }
  };

  const saveSchedule = async (patch) => {
    setSavingSchedule(true);
    try {
      const payload = {
        ...schedule,
        ...patch,
        recipients: (patch?.recipients ?? recipientsText)
          .toString().split(/[,;\s]+/).map(s => s.trim()).filter(Boolean),
      };
      const { data } = await axios.put(`${API}/schedule`, payload);
      setSchedule(data);
      toast.success('Envoi automatique enregistré');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Enregistrement impossible');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleSendNow = async () => {
    setSendingNow(true);
    try {
      const { data } = await axios.post(`${API}/send-now`, null, {
        params: { year: periodParams?.year, month: periodParams?.month },
      });
      toast.success(`Envoyé à ${(data.recipients_ok || []).length} destinataire(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Envoi impossible');
    } finally {
      setSendingNow(false);
    }
  };

  const r = report?.report;

  return (
    <Card className="mt-6 border-blue-200" data-testid="operational-ai-card">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-blue-100 shrink-0">
              <Activity className="w-5 h-5 text-blue-700" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-lg">Analyse opérationnelle IA — rapport d'exploitation</CardTitle>
              <CardDescription>
                L'IA analyse les volumes transportés, la ponctualité des livraisons, la fiabilité
                des transporteurs, la qualité documentaire (bons signés) et les incidents.
                Le PDF embarque les <strong>graphiques d'exploitation</strong> (opérations, tonnage,
                kilométrage, statuts, axes, transporteurs, incidents).
                Période analysée : <strong>{periodLabel || '—'}</strong>.
              </CardDescription>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Button
              variant="outline" onClick={handleGenerate} disabled={generating || !periodReady}
              data-testid="generate-operational-analysis"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
              {r ? 'Relancer l\'analyse' : 'Générer l\'analyse'}
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700" onClick={handleDownloadPdf}
              disabled={downloading || !periodReady} data-testid="download-operational-analysis-pdf"
            >
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Rapport PDF
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-5 space-y-5">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <RefreshCw className="w-4 h-4 animate-spin" /> Chargement de la dernière analyse…
          </div>
        )}

        {!loading && !r && (
          <p className="text-sm text-gray-500" data-testid="operational-analysis-empty">
            Aucune analyse opérationnelle enregistrée pour {periodLabel || 'cette période'}.
            Cliquez sur « Générer l'analyse » — le rapport sera ensuite téléchargeable en PDF avec
            ses graphiques.
          </p>
        )}

        {r && (
          <div className="space-y-5" data-testid="operational-analysis-content">
            <div className="rounded-lg bg-blue-50/60 border border-blue-100 p-4">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-blue-700 mb-1">
                Synthèse exécutive · {report.periodLabel} · {report.aiProvider}/{report.aiModel}
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">{r.synthese}</p>
            </div>

            {r.dashboard?.length > 0 && (
              <div className="grid gap-2 sm:grid-cols-2" data-testid="operational-analysis-dashboard">
                {r.dashboard.map((d, i) => (
                  <div key={i} className="border rounded-lg p-3 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-gray-600 truncate">{d.indicateur}</p>
                      <Badge variant="outline" className={`text-[10px] ${STATUS_STYLE[d.statut] || ''}`}>
                        {d.statut}
                      </Badge>
                    </div>
                    <p className="text-base font-bold text-gray-900">{d.valeur}</p>
                    <p className="text-[11px] text-gray-500">{d.variation} · {d.lecture}</p>
                  </div>
                ))}
              </div>
            )}

            {r.actions_operationnelles?.length > 0 && (
              <div data-testid="operational-analysis-actions">
                <p className="text-sm font-semibold text-gray-800 mb-2">Actions opérationnelles à mener</p>
                <div className="space-y-2">
                  {r.actions_operationnelles.map((a, i) => (
                    <div key={i} className="border rounded-lg p-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-blue-600 text-[10px]">P{a.priorite}</Badge>
                        <p className="text-sm font-semibold text-gray-900 truncate">{a.cible}</p>
                        {a.responsable && (
                          <span className="text-[11px] text-gray-500">· {a.responsable}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{a.constat}</p>
                      <p className="text-sm text-gray-800 mt-1">→ {a.action}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {r.risques?.length > 0 && (
              <div data-testid="operational-analysis-risks">
                <p className="text-sm font-semibold text-gray-800 mb-2">Risques prioritaires</p>
                <ul className="space-y-1.5">
                  {r.risques.map((x, i) => (
                    <li key={i} className="text-xs text-gray-700 flex gap-2">
                      <TriangleAlert className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${LEVEL_STYLE[x.niveau] || ''}`} />
                      <span><strong>{x.titre}</strong> — {x.impact}. {x.mitigation}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {canConfigure && schedule && (
          <div className="border-t pt-4 space-y-3" data-testid="operational-analysis-schedule">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-700" /> Envoi automatique mensuel
                </p>
                <p className="text-xs text-gray-500">
                  Le {schedule.send_day} de chaque mois : rapport PDF opérationnel du mois écoulé
                  (graphiques inclus), envoyé aux destinataires ci-dessous.
                </p>
              </div>
              <Switch
                checked={!!schedule.enabled}
                onCheckedChange={(v) => saveSchedule({ enabled: v })}
                disabled={savingSchedule}
                data-testid="operational-analysis-schedule-toggle"
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
              <div className="min-w-0">
                <Label className="text-xs">Destinataires (séparés par des virgules)</Label>
                <Input
                  value={recipientsText}
                  onChange={(e) => setRecipientsText(e.target.value)}
                  placeholder="direction@baobab.ci, ops@baobab.ci"
                  className="h-9 text-sm"
                  data-testid="operational-analysis-recipients"
                />
              </div>
              <div>
                <Label className="text-xs">Jour</Label>
                <Input
                  type="number" min={1} max={28} value={schedule.send_day}
                  onChange={(e) => setSchedule({ ...schedule, send_day: Number(e.target.value) })}
                  className="h-9 text-sm w-20"
                  data-testid="operational-analysis-send-day"
                />
              </div>
              <div className="flex items-end gap-2">
                <Button
                  variant="outline" onClick={() => saveSchedule({})} disabled={savingSchedule}
                  data-testid="operational-analysis-save-schedule"
                >
                  {savingSchedule ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Enregistrer
                </Button>
                <Button
                  variant="outline" onClick={handleSendNow} disabled={sendingNow}
                  data-testid="operational-analysis-send-now"
                >
                  {sendingNow ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                  Envoyer maintenant
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OperationalAiAnalysisCard;
